/**
 * @todo convert all to A+ Promises
 */

import { _throwNotImplementedException } from './COM';
import { Rs232DisplayAdapter } from './Rs232DisplayAdapter';
import { Rs232Reply } from './Rs232Reply';
import { $ } from '../../../vendor/jquery';
import { objectHasOwnProperty } from '../../Utility';

// <editor-fold desc="Enumerations">

/**
 * Enumeration for destination field in SamsungPacket
 * @readonly
 * @enum {number}
 */
var SamsungDestination = {
    All: 0xfe,
    Device0: 0xff,
};

/**
 * Enumeration for command type field in SamsungCommand
 * @readonly
 * @enum {int}
 */
var SamsungCommandType = {
    NoOp: 0xff,
    GetModelName: 0x8a,
    GetSerialNo: 0x0b,
    GetPowerStatus: 0x11,
    SetPowerStatus: 0x11,
    GetMuteStatus: 0x13,
    SetMuteStatus: 0x13,
    SetVolume: 0x12,
    GetVolume: 0x12,
    GetSelectedInput: 0x14,
    SetSelectedInput: 0x14,
};

/**
 * Enumeration for power state commands
 * @readonly
 * @enum {Uint8Array}
 */
var SamsungPowerState = {
    On: new Uint8Array([0x01]),
    Off: new Uint8Array([0x00]),
};

/**
 * Enumeration for inputs
 * @readonly
 * @enum {number}
 */
var SamsungInput = {
    NoMean: 0x00,
    PC: 0x14,
    VGA: 0x14,
    BNC: 0x1e,
    DVI: 0x18,
    AV: 0x0c,
    SVideo: 0x04,
    Component: 0x08,
    MagicNet: 0x20,
    DVI_VIDEO: 0x1f,
    RF: 0x30,
    DTV: 0x40,
    HDMI: 0x21,
    parseInt: function (value) {
        for (var prop in SamsungInput) {
            if (objectHasOwnProperty(SamsungInput, prop) && SamsungInput[prop] === value) {
                return prop;
            }
        }

        return 'unknown';
    },
};

/**
 * Enumeration for mute state
 * @readonly
 * @enum {Uint8Array}
 */
var SamsungMuteState = {
    Mute: new Uint8Array([0x01]),
    Unmute: new Uint8Array([0x00]),
};

// </editor-fold>

// <editor-fold desc="SamsungRs232Reply Abstract Class">

/**
 * Generic Rs232Reply for Samsung displays, represents a reply packet from the
 * display
 * @class
 * @param {SamsungPacket} packet - the bytes received from the display
 */
function SamsungRs232Reply(packet) {
    Rs232Reply.call(this, packet);

    this.header = packet.header;

    this.command = packet.command;

    this.destination = packet.destination;

    this.dataLength = packet.dataLength;

    this.data = packet.data;

    this.checksum = packet.checksum;
}

SamsungRs232Reply.prototype = Object.create(Rs232Reply.prototype);
SamsungRs232Reply.prototype.constructor = SamsungRs232Reply;

SamsungRs232Reply.prototype.verifyPacket = function () {
    var runningChecksum = this.command + this.destination + this.dataLength;

    if (this.data && this.data.length) {
        for (var i = 0; i < this.data.length; ++i) {
            runningChecksum = runningChecksum + this.data[i];
        }
    }

    //take right-most byte
    runningChecksum = runningChecksum & 0xff;

    if (runningChecksum != this.checksum) {
        throw new Error(
            'invalid checksum. Expected `' + this.checksum + '`, got `' + runningChecksum + '`'
        );
    }
};

SamsungRs232Reply.prototype.toString = _throwNotImplementedException('SamsungRs232Reply.toString');
// </editor-fold>

// <editor-fold desc="SamsungGetModelReply Class">
/**
 *
 * @param {SamsungPacket} packet
 * @constructor
 */
function SamsungGetModelReply(packet) {
    SamsungRs232Reply.call(this, packet);

    //verify the packet integrity
    this.verifyPacket();

    this.model = String.fromCharCode.apply(String, packet.data);
}

SamsungGetModelReply.prototype = Object.create(SamsungRs232Reply.prototype);
SamsungGetModelReply.prototype.constructor = SamsungGetModelReply;

SamsungGetModelReply.prototype.toString = function () {
    return this.model;
};
// </editor-fold>

// <editor-fold desc="SamsungGetSerialNumberReply Class">
function SamsungGetSerialNumberReply(packet) {
    SamsungRs232Reply.call(this, packet);

    //verify the packet integrity
    this.verifyPacket();

    this.serialNumber = String.fromCharCode.apply(String, packet.data);
}

SamsungGetSerialNumberReply.prototype = Object.create(SamsungRs232Reply.prototype);
SamsungGetSerialNumberReply.prototype.constructor = SamsungGetSerialNumberReply;

SamsungGetSerialNumberReply.prototype.toString = function () {
    return this.serialNumber;
};
// </editor-fold>

// <editor-fold desc="SamsungGetPowerStateReply Class">
function SamsungGetPowerStateReply(packet) {
    SamsungRs232Reply.call(this, packet);

    //verify the packet integrity
    this.verifyPacket();

    this.isAck = this.data[0] === 0x41;
    this.rCmd = this.data[1];

    if (!this.isAck) {
        throw new Error('device returned nak response with error code `' + this.data[2] + '`');
    }

    if (this.rCmd !== 0x11) {
        throw new Error(
            'device returned a response with an invalid r-CMD code `' + this.rCmd + '`'
        );
    }

    this.powerState = this.data[2];

    this.currentPowerModeTranslated = 'unknown';

    switch (this.powerState) {
        case 0:
            this.currentPowerModeTranslated = 'off';
            break;
        case 1:
            this.currentPowerModeTranslated = 'on';
            break;
    }
}

SamsungGetPowerStateReply.prototype = Object.create(SamsungRs232Reply.prototype);
SamsungGetPowerStateReply.prototype.constructor = SamsungGetPowerStateReply;

SamsungGetPowerStateReply.prototype.toString = function () {
    return this.currentPowerModeTranslated;
};
// </editor-fold>

// <editor-fold desc="SamsungGetParameterReply Class">
function SamsungGetParameterReply(bytes) {
    SamsungRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.success = false;

    this.startOfMessage = this.message[0];

    this.resultCode = parseInt(String.fromCharCode.apply(String, this.message.slice(1, 3)), 16);

    if (this.resultCode === 0) {
        this.success = true;

        this.operationCodePage = String.fromCharCode.apply(String, this.message.slice(3, 5));

        this.operationCode = String.fromCharCode.apply(String, this.message.slice(5, 7));

        this.operationTypeCode = String.fromCharCode.apply(String, this.message.slice(7, 9));

        //todo: i believe these are always ASCII-encoded integers, but I'm not
        //sure. we'll do the integer conversion
        this.maximumValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(9, 13)),
            16
        );
        this.requestedValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(13, 17)),
            16
        );
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

SamsungGetParameterReply.prototype = Object.create(SamsungRs232Reply.prototype);
SamsungGetParameterReply.prototype.constructor = SamsungGetParameterReply;

SamsungGetParameterReply.prototype.toString = _throwNotImplementedException(
    'SamsungGetParameterReply.toString'
);
// </editor-fold>

// <editor-fold desc="SamsungGetMuteStateReply Class">
function SamsungGetMuteStateReply(packet) {
    SamsungRs232Reply.call(this, packet);

    //verify the packet integrity
    this.verifyPacket();

    this.isAck = this.data[0] === 0x41;
    this.rCmd = this.data[1];

    if (!this.isAck) {
        throw new Error('device returned nak response with error code `' + this.data[2] + '`');
    }

    if (this.rCmd !== 0x13) {
        throw new Error(
            'device returned a response with an invalid r-CMD code `' + this.rCmd + '`'
        );
    }

    this.muteState = this.data[2];

    this.currentMuteStateTranslated = 'unknown';

    switch (this.muteState) {
        case 0:
            this.currentMuteStateTranslated = 'unmute';
            break;
        case 1:
            this.currentMuteStateTranslated = 'mute';
            break;
    }
}

SamsungGetMuteStateReply.prototype = Object.create(SamsungGetParameterReply.prototype);
SamsungGetMuteStateReply.prototype.constructor = SamsungGetMuteStateReply;

SamsungGetMuteStateReply.prototype.toString = function () {
    return this.currentMuteStateTranslated;
};
// </editor-fold>

// <editor-fold desc="SamsungGetVideoInputReply Class">
function SamsungGetVideoInputReply(packet) {
    SamsungRs232Reply.call(this, packet);

    //verify the packet integrity
    this.verifyPacket();

    this.isAck = this.data[0] === 0x41;
    this.rCmd = this.data[1];

    if (!this.isAck) {
        throw new Error('device returned nak response with error code `' + this.data[2] + '`');
    }

    if (this.rCmd !== 0x14) {
        throw new Error(
            'device returned a response with an invalid r-CMD code `' + this.rCmd + '`'
        );
    }

    this.selectedInputRaw = this.data[2];
    this.selectedInput = SamsungInput.parseInt(this.data[2]);
}

SamsungGetVideoInputReply.prototype = Object.create(SamsungGetParameterReply.prototype);
SamsungGetVideoInputReply.prototype.constructor = SamsungGetVideoInputReply;

SamsungGetVideoInputReply.prototype.toString = function () {
    return this.selectedInput;
};
// </editor-fold>

// <editor-fold desc="SamsungGetVolumeReply Class">
function SamsungGetVolumeReply(packet) {
    SamsungRs232Reply.call(this, packet);

    //verify the packet integrity
    this.verifyPacket();

    this.isAck = this.data[0] === 0x41;
    this.rCmd = this.data[1];

    if (!this.isAck) {
        throw new Error('device returned nak response with error code `' + this.data[2] + '`');
    }

    if (this.rCmd !== 0x12) {
        throw new Error(
            'device returned a response with an invalid r-CMD code `' + this.rCmd + '`'
        );
    }

    this.requestedValue = this.data[2];
}

SamsungGetVolumeReply.prototype = Object.create(SamsungGetParameterReply.prototype);
SamsungGetVolumeReply.prototype.constructor = SamsungGetVolumeReply;

SamsungGetVolumeReply.prototype.toString = function () {
    return this.requestedValue;
};
// </editor-fold>

// <editor-fold desc="SamsungSetParameterReply Class">
function SamsungSetParameterReply(bytes) {
    SamsungRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.success = false;
    this.requestedValue = 'failure';

    this.startOfMessage = this.message[0];

    this.resultCode = parseInt(String.fromCharCode.apply(String, this.message.slice(1, 3)), 16);

    if (this.resultCode === 0) {
        this.success = true;

        this.replyCommand = String.fromCharCode.apply(String, this.message.slice(3, 9));

        this.maximumValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(9, 13)),
            16
        );

        this.requestedValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(13, 17)),
            16
        );
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

SamsungSetParameterReply.prototype = Object.create(SamsungRs232Reply.prototype);
SamsungSetParameterReply.prototype.constructor = SamsungSetParameterReply;

SamsungSetParameterReply.prototype.toString = function () {
    if (!this.stringValue) {
        SamsungRs232Reply.toString.call(this); //what would base class do?
    }

    return this.stringValue;
};
// </editor-fold>

// <editor-fold desc="SamsungCommand Class">
function SamsungCommand(command, destination, data) {
    SamsungPacket.call(this, command, destination);

    if (data) {
        this.data = data;
    }
}

SamsungCommand.prototype = Object.create(SamsungPacket.prototype);
SamsungCommand.prototype.constructor = SamsungCommand;
// </editor-fold>

// <editor-fold desc="SamsungPacket">
function SamsungPacket(command, destination) {
    this.header = 0xaa;

    if (!command) {
        throw new Error('must specify command parameter in SamsungPacket()');
    }

    this.command = command;

    this.destination = destination || SamsungDestination.All; //default destination 0xFE is "all devices"

    this.dataLength = 0; //to compute later

    this.data = new Uint8Array(0);

    this.checksum = 0; //to compute later

    this.rawBytes = [];
}

SamsungPacket.prototype.readPacket = function (controller, iteration, bytesThusFar, jqDeferred) {
    iteration = iteration || 0;

    if (typeof bytesThusFar === 'undefined') {
        bytesThusFar = [];
    }

    if (!jqDeferred) {
        jqDeferred = $.Deferred();
    }

    if (++iteration > 1000) {
        jqDeferred.reject('too many iterations');

        return;
    }

    controller.read(1).then(
        function (bytes) {
            var theByte = bytes[0];

            switch (bytesThusFar.length) {
                case 0:
                    this.header = theByte;

                    break; //header
                case 1:
                    this.command = theByte;

                    break; //command
                case 2:
                    this.destination = theByte;

                    break; //ID
                case 3:
                    this.dataLength = theByte;
                    this.data = new Uint8Array(this.dataLength);

                    break; //data length
                default:
                    if (bytesThusFar.length > this.data.length + 3) {
                        this.checksum = theByte;

                        bytesThusFar.push(theByte);

                        this.rawBytes = bytesThusFar;

                        jqDeferred.resolve(this);

                        return;
                    } else {
                        this.data[bytesThusFar.length - 4] = theByte;
                    }

                    break;
            }

            bytesThusFar.push(theByte);

            this.readPacket(controller, iteration, bytesThusFar, jqDeferred);
        }.bind(this),
        function () {
            this.rawBytes = bytesThusFar;

            jqDeferred.reject(this);
        }.bind(this)
    );

    return jqDeferred.promise();
};

SamsungPacket.prototype.toUint8Array = function () {
    var i;

    var dataLength = this.data ? this.data.length : 0;

    var payload = new Uint8Array(5 + dataLength);
    payload[0] = this.header;
    payload[1] = this.command;
    payload[2] = this.destination;
    payload[3] = dataLength;

    for (i = 0; i < dataLength; ++i) {
        payload[4 + i] = this.data[i];
    }

    this.checksum = payload[1];
    for (i = 2; i < payload.length - 1; ++i) {
        this.checksum = this.checksum + payload[i];
    }

    //note: take right-most byte
    this.checksum = this.checksum & 0xff;

    payload[4 + dataLength] = this.checksum;

    return payload;
};
// </editor-fold>

/**
 * Generic SamsungRs232DisplayAdapter
 * @class
 * @param {string} displayMake - the vendor name of the hardware display
 * @param {string|null} displayModel - the model of the display
 * @param {Rs232Controller} rs232Controller - controller for interacting with
 * COM port
 */
function SamsungRs232DisplayAdapter(displayMake, displayModel, rs232Controller) {
    this.make = 'Samsung';

    this.model = displayModel;

    this.controller = rs232Controller;
}

SamsungRs232DisplayAdapter.prototype = Object.create(Rs232DisplayAdapter.prototype);
SamsungRs232DisplayAdapter.prototype.constructor = SamsungRs232DisplayAdapter;

// <editor-fold desc="Rs232DisplayAdapter Methods">

SamsungRs232DisplayAdapter.prototype.getModel = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var bytes = new SamsungCommand(
                SamsungCommandType.GetModelName,
                SamsungDestination.Device0
            ).toUint8Array();

            this.controller.write(bytes);

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    try {
                        jqDeferred.resolve(new SamsungGetModelReply(packet));
                    } catch (replyParseError) {
                        jqDeferred.reject(replyParseError);
                    }
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.getSerialNumber = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var bytes = new SamsungCommand(
                SamsungCommandType.GetSerialNo,
                SamsungDestination.Device0
            ).toUint8Array();

            this.controller.write(bytes);

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    jqDeferred.resolve(new SamsungGetSerialNumberReply(packet));
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.getPowerState = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var bytes = new SamsungCommand(
                SamsungCommandType.GetPowerStatus,
                SamsungDestination.Device0
            ).toUint8Array();

            this.controller.write(bytes);

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    jqDeferred.resolve(new SamsungGetPowerStateReply(packet));
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.setPowerState = function (desiredPowerState) {
    var jqDeferred = $.Deferred();

    //will wait until we are allow to execute commands
    this.waitForIdle().then(
        function () {
            desiredPowerState = desiredPowerState.toLowerCase();
            if (desiredPowerState !== 'on' && desiredPowerState !== 'off') {
                jqDeferred.reject(
                    'invalid requested power state `' +
                        desiredPowerState +
                        '`. must be either `on` or `off`'
                );
            }

            this.controller.write(
                new SamsungCommand(
                    SamsungCommandType.SetPowerStatus,
                    SamsungDestination.Device0,
                    desiredPowerState === 'on' ? SamsungPowerState.On : SamsungPowerState.Off
                ).toUint8Array()
            );

            new SamsungPacket(SamsungCommandType.NoOp).readPacket(this.controller).then(
                function (packet) {
                    this.setRefractoryPeriod(10000);

                    jqDeferred.resolve(new SamsungGetPowerStateReply(packet));
                }.bind(this),
                jqDeferred.reject
            );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.getMuted = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var bytes = new SamsungCommand(
                SamsungCommandType.GetMuteStatus,
                SamsungDestination.Device0
            ).toUint8Array();

            this.controller.write(bytes);

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    jqDeferred.resolve(new SamsungGetMuteStateReply(packet));
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.setMuted = function (desiredMuteState) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            desiredMuteState = desiredMuteState.toLowerCase();
            if (desiredMuteState !== 'mute' && desiredMuteState !== 'unmute') {
                jqDeferred.reject(
                    'invalid requested mute state `' +
                        desiredMuteState +
                        '`. must be either `mute` or `unmute`'
                );
            }

            this.controller.write(
                new SamsungCommand(
                    SamsungCommandType.SetMuteStatus,
                    SamsungDestination.Device0,
                    desiredMuteState === 'mute' ? SamsungMuteState.Mute : SamsungMuteState.Unmute
                ).toUint8Array()
            );

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    jqDeferred.resolve(new SamsungGetMuteStateReply(packet));
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.getAudioVolume = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var bytes = new SamsungCommand(
                SamsungCommandType.GetVolume,
                SamsungDestination.Device0
            ).toUint8Array();

            this.controller.write(bytes);

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    jqDeferred.resolve(new SamsungGetVolumeReply(packet));
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.setAudioVolume = function (desiredAudioVolume) {
    desiredAudioVolume = parseInt(desiredAudioVolume);

    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            if (desiredAudioVolume < 0 || desiredAudioVolume > 100) {
                jqDeferred.reject(
                    'invalid requested volume `' +
                        desiredAudioVolume +
                        '`. must be between `0` and `100`'
                );
            }

            this.controller.write(
                new SamsungCommand(
                    SamsungCommandType.SetVolume,
                    SamsungDestination.Device0,
                    new Uint8Array([desiredAudioVolume])
                ).toUint8Array()
            );

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    jqDeferred.resolve(new SamsungGetVolumeReply(packet));
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.getVideoInput = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var bytes = new SamsungCommand(
                SamsungCommandType.GetSelectedInput,
                SamsungDestination.Device0
            ).toUint8Array();

            this.controller.write(bytes);

            new SamsungPacket(SamsungCommandType.NoOp)
                .readPacket(this.controller)
                .then(function (packet) {
                    jqDeferred.resolve(new SamsungGetVideoInputReply(packet));
                }, jqDeferred.reject);
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

SamsungRs232DisplayAdapter.prototype.setVideoInput = function (desiredVideoInput) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            if (!SamsungInput[desiredVideoInput]) {
                jqDeferred.reject(
                    'invalid requested input `' +
                        desiredVideoInput +
                        '`. Must be one of SamsungInput enumeration.'
                );
            }

            this.controller.write(
                new SamsungCommand(
                    SamsungCommandType.SetSelectedInput,
                    SamsungDestination.Device0,
                    new Uint8Array([SamsungInput[desiredVideoInput]])
                ).toUint8Array()
            );

            //NOTE: depending on which input we are switching to, it may take the
            //      device some time to send the reply, so we will attempt to read
            //      the reply 10 times (~1 second if readtime is 100 ms)
            var tryReadPacket = function (attemptNumber) {
                if (!attemptNumber) {
                    attemptNumber = 0;
                }

                if (attemptNumber > 9) {
                    jqDeferred.reject(
                        'the device did not respond in a timely manner when attempt to set the video input'
                    );

                    return;
                }

                ++attemptNumber;

                new SamsungPacket(SamsungCommandType.NoOp).readPacket(this.controller).then(
                    function (packet) {
                        jqDeferred.resolve(new SamsungGetVideoInputReply(packet));
                    },
                    function () {
                        tryReadPacket(attemptNumber);
                    }
                );
            }.bind(this);

            tryReadPacket();
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

// </editor-fold>

//tell the Rs232DisplayAdapter service about the "Samsung" brand of displays
Rs232DisplayAdapter.registerVendorAdapter('samsung', SamsungRs232DisplayAdapter);

export { SamsungRs232DisplayAdapter };
